'use client'

import { useTranslations } from 'next-intl'
import { MouseEvent } from 'react'

import {
  cn,
  ExpandButton,
  isNilOrEmptyString,
  useExpand,
  useFormatDate,
} from '@/shared/lib'
import { Badge, Icon } from '@/shared/ui'

import { MobileDeleteButton } from './delete-button.mobile'
import { History } from '../../model/search-history-store/history'
import { useSearchHistoryStore } from '../../model/search-history-store/store'

const INITIAL_COUNT = 3

const HistoryBadges = ({ child }: { child: History['child'] }) => {
  if (child.length === 0) return null

  return (
    <ul className="flex gap-1">
      {child[0].type === 'badge' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Badge variant="solid" color={item.color}>
              {item.shortName}
            </Badge>
          </li>
        ))}
      {child[0].type === 'icon' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Icon name={item.name} size={18} className={item.color} />
          </li>
        ))}
    </ul>
  )
}

const DateRange = ({
  startDate,
  endDate,
}: {
  startDate: string
  endDate: string
}) => {
  const { formatDate } = useFormatDate()

  if (isNilOrEmptyString(startDate) || isNilOrEmptyString(endDate)) return null

  return (
    <p className="typo-caption2 font-normal text-gray-500">
      {formatDate({ date: startDate, format: 'yyyy.MM.dd' })}~
      {formatDate({ date: endDate, format: 'yyyy.MM.dd' })}
    </p>
  )
}

type Props = {
  onItemClick: (history: History) => void
  className?: string
}

export const MobileLocationHistoryList = ({
  onItemClick,
  className,
}: Props) => {
  const t = useTranslations('map_search')

  const { expanded, toggleExpand } = useExpand({
    defaultExpanded: false,
  })

  const searchHistory = useSearchHistoryStore(state => state.searchHistory)
  const searchHistoryActions = useSearchHistoryStore(state => state.actions)

  if (searchHistory.length === 0) return null

  const displayedHistory = expanded
    ? searchHistory
    : searchHistory.slice(0, INITIAL_COUNT)

  const handleItemClick = (history: History) => () => {
    onItemClick(history)
  }

  const handleDeleteItem = (id: string) => (event: MouseEvent) => {
    event.stopPropagation()
    searchHistoryActions.deleteSearchHistory(id)
  }

  const handleDeleteAll = () => {
    searchHistoryActions.clearSearchHistory()
  }

  return (
    <section className={cn(className)}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="typo-headline3 font-semibold text-black">
          {t('recent_search.title')}
        </h2>
        <MobileDeleteButton onClick={handleDeleteAll} />
      </div>
      <ul>
        {displayedHistory.map(history => (
          <li key={history.id} className="group">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 py-2.5 group-first:pt-0 group-last:pb-0 hover:bg-purple-50"
              onClick={handleItemClick(history)}
            >
              <div className="flex min-w-0 gap-2">
                <Icon
                  name="clock"
                  size={18}
                  className="relative top-[2.5px] shrink-0 text-gray-400"
                />
                <div className="overflow-hidden text-left">
                  <div className="mb-0.5 flex items-center gap-1">
                    <p className="typo-body2 truncate font-normal text-black">
                      {history.name || t('recent_search.default_keyword')}
                    </p>
                    <HistoryBadges child={history.child} />
                  </div>
                  <DateRange
                    startDate={history.startDate}
                    endDate={history.endDate}
                  />
                </div>
              </div>
              <div role="button" onClick={handleDeleteItem(history.id)}>
                <Icon name="close" size={16} className="text-gray-500" />
              </div>
            </button>
          </li>
        ))}
      </ul>
      {searchHistory.length > INITIAL_COUNT && (
        <div className="mt-6 flex justify-center">
          <ExpandButton expanded={expanded} onClick={toggleExpand} />
        </div>
      )}
    </section>
  )
}
